import { Icon, Icons, Intent } from "construct-ui";
import languageModel from "../../models/language.model";
import SoldToModel from "../../models/sold-to/sold-to.model";
import SimpleSearchDialog from "./simple-search.dialog";

/**
 * SoldToSearchInputComponent
 * 
 * Queries for a list of Sold-To codes.
 * Has search functionality with infinite scrolling and supports both single and multiple items.
 * 
 * @author Darrus Goh
 */
export default function SoldToSearchInputComponent() {
    const DEFAULT_ONSELECT = (selectedItems) => { console.log('SoldToSearchInputComponent: onSelect(selectedItems | selectedItem)') };

    let isOpen = false;
    let isSearching = false;
    let items = [];
    let totalItemsCount = 0;

    const openDialog = () => {
        isOpen = true;
    }

    const onClose = () => {
        isOpen = false;
    }

    const onSearch = (searchText, pageIndex, pageSize) => {
        return getSoldToList(searchText, pageIndex, pageSize, true);
    };

    const onClear = () => {
        items = [];
    }

    const onInfiniteScroll = (searchText, pageIndex, pageSize) => {
        if (totalItemsCount > items.length) {
            return getSoldToList(searchText, pageIndex, pageSize, false);
        }
    }

    const getSoldToList = (searchText, pageIndex, pageSize, refresh = true) => {
        if (isSearching) return Promise.resolve();

        isSearching = true;
        return SoldToModel.getListSoldToCode(searchText, { pageIndex, pageSize })
            .then((result) => {
                let resultItems = result.data.map(({ soldToCode, soldToDisplay }) => { return { code: soldToCode, name: soldToDisplay } });
                totalItemsCount = result.total;

                if (refresh) {
                    items = resultItems;
                } else {
                    items = items.concat(resultItems);
                }
            })
            .finally(() => {
                isSearching = false;
            });
    }

    const renderLabelMulti = (selectedItems, itemValue) => {
        if (selectedItems.length === 1 && selectedItems[0] === '') {
            return languageModel.getText('LT00000286', 'All items selected');
        }
        else if (selectedItems.length > 3) {
            return `${selectedItems.length} ${languageModel.getText("LT00000308", "items selected")}`
        }
        else if (selectedItems.length > 0) {
            return selectedItems.map(item => item[itemValue]).join(', ');
        }
        return '';
    }

    const renderLabelSingle = (selectedItem, itemValue) => {
        if (selectedItem) {
            return selectedItem[itemValue];
        }
        return '';
    }

    const renderLabel = [renderLabelSingle, renderLabelMulti];

    return {
        view: ({ attrs: {
            selectedItems = [],
            selectedItem = null,
            multiple = false,
            disabled = false,
            required = false,
            itemKey = 'code',
            itemValue = 'name',
            onSelect = DEFAULT_ONSELECT } }) => {
            return [
                m('.d-flex.gap-4.justify-content-between.align-items-center.form-control.form-control-bg.cursor-pointer', { onclick: openDialog },
                    m('input.flex-fill.text-start.cursor-pointer.ps-0', {
                        type: 'text',
                        style: {
                            width: 'inherit',
                            'overflow-x': 'hidden',
                            'text-overflow': 'ellipsis',
                            border: 'none',
                            background: 'transparent'
                        },
                        oninput: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        },
                        placeholder: languageModel.getText('LT00000719', 'No items selected'),
                        value: renderLabel[+multiple](multiple ? selectedItems : selectedItem, itemValue),
                        required,
                        disabled
                    }),
                    m(Icon,
                        {
                            name: Icons.LIST,
                            intent: Intent.PRIMARY,
                            onclick: openDialog
                        }
                    )
                ),
                m(SimpleSearchDialog, {
                    title: languageModel.getText('LT00000835', 'Sold-To'),
                    searchPlaceholderText: languageModel.getText('LT00000826', 'Sold-To Code / Name'),
                    items,
                    isOpen,
                    selectedItems,
                    selectedItem,
                    multiple,
                    totalItemsCount,
                    isSearching,
                    itemKey,
                    itemValue,
                    onSearch,
                    onInfiniteScroll,
                    onSelect,
                    onClose,
                    onClear
                })
            ]
        }
    };
}